'use client';

import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import { POPULAR_LOCATIONS, LocationPreset } from '@/lib/location/resolver';
import { CreateBirthProfileInput } from '@/lib/api/client';
import { Calendar, Clock, Sparkles, AlertCircle } from 'lucide-react';
import { LocationAutocomplete } from './LocationAutocomplete';

interface QuickCalculatorFormProps {
  title: string;
  subtitle: string;
  onCalculate: (input: CreateBirthProfileInput) => void;
  loading: boolean;
}

export function QuickCalculatorForm({
  title,
  subtitle,
  onCalculate,
  loading
}: QuickCalculatorFormProps) {
  const tForm = useTranslations('form');

  const [birthDate, setBirthDate] = useState('1990-01-01');
  const [birthTime, setBirthTime] = useState('12:00:00');

  const [locationName, setLocationName] = useState(tForm('presetDelhi'));
  const [confirmedPreset, setConfirmedPreset] = useState<LocationPreset | null>(POPULAR_LOCATIONS[0]);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSelectLocation = (preset: LocationPreset) => {
    setConfirmedPreset(preset);
    setLocationName(preset.name);
    setFormError(null);
  };

  const handleClearLocation = () => {
    setConfirmedPreset(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!confirmedPreset) {
      setFormError(tForm('selectLocationError'));
      return;
    }

    onCalculate({
      name: title,
      birthDate,
      birthTime,
      latitude: confirmedPreset.latitude,
      longitude: confirmedPreset.longitude,
      timezone: confirmedPreset.timezone,
      locationName: confirmedPreset.name
    });
  };

  return (
    <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 sm:p-8 shadow-xl relative overflow-hidden">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[#5ab4ff] text-[11px] font-bold uppercase tracking-[0.12em] mb-2">
          <Sparkles className="w-4 h-4 text-[#5ab4ff]" />
          <span>{tForm('vedicCalc')}</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-white mb-2">
          {title}
        </h1>
        <p className="text-[rgba(220,236,255,0.85)] text-xs sm:text-sm max-w-2xl leading-[1.6]">
          {subtitle}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Birth Date */}
          <div>
            <label htmlFor="quick-birth-date" className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 flex items-center">
              <Calendar className="w-3.5 h-3.5 inline mr-1.5 text-[#5ab4ff]" />
              {tForm('birthDate')}
            </label>
            <input
              id="quick-birth-date"
              type="date"
              required
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 px-4 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] transition-all [color-scheme:dark]"
            />
          </div>

          {/* Birth Time */}
          <div>
            <label htmlFor="quick-birth-time" className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 flex items-center">
              <Clock className="w-3.5 h-3.5 inline mr-1.5 text-[#5ab4ff]" />
              {tForm('birthTime')}
            </label>
            <input
              id="quick-birth-time"
              type="time"
              step="1"
              required
              value={birthTime}
              onChange={(e) => setBirthTime(e.target.value)}
              className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 px-4 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] transition-all [color-scheme:dark]"
            />
          </div>
        </div>

        {/* Centralized Autocomplete Location Picker */}
        <div>
          <label className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 block">
            {tForm('birthLocation')} <span className="text-[#5ab4ff]">*</span>
          </label>
          <LocationAutocomplete
            value={locationName}
            onSelectLocation={handleSelectLocation}
            onClearLocation={handleClearLocation}
            confirmedPreset={confirmedPreset}
            idPrefix="quick-loc"
          />
        </div>

        {formError && (
          <div className="bg-rose-500/10 border border-[#ff9bb0]/30 rounded-xl p-3.5 text-[#ff9bb0] text-xs flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 text-[#ff9bb0]" />
            <span>{formError}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full h-12 rounded-full bg-white text-[#03163d] font-semibold text-sm hover:bg-white/90 disabled:opacity-50 transition-all cursor-pointer shadow-[0_0_25px_rgba(150,165,255,0.4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ab4ff] active:scale-[0.98]"
        >
          {loading ? tForm('calculating') : tForm('calculateNow')}
        </button>
      </form>
    </div>
  );
}
